import React, { useState, useRef } from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { useGSAP } from '@gsap/react';

import { useTheme } from 'hooks/useTheme';
import useMediaQuery from 'hooks/useMediaQuery';
import ImageCard from 'components/ImageCard';
import SmallStepIndicator from '../components/SmallStepIndicator';

gsap.registerPlugin(Draggable);

const CARD_GAP = 20;

const ThinImagesSection = ({ cards, titleItalic, titleGrotesk }) => {
  const { isDarkMode } = useTheme();
  const isMobile = useMediaQuery();
  const carouselContainerRef = useRef(null);
  const cardWidthRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);

  useGSAP(
    () => {
      if (!isMobile || !carouselContainerRef.current) return;

      const container = carouselContainerRef.current;
      const timer = setTimeout(() => {
        const firstCard = container.querySelector('.thin-cards-carousel-slide');
        if (!firstCard || firstCard.offsetWidth === 0) return;

        const rawCardWidth = firstCard.offsetWidth;
        const fullSlideWidth = rawCardWidth + CARD_GAP;

        cardWidthRef.current = fullSlideWidth;

        const dragInstance = Draggable.create(container, {
          type: 'x',
          inertia: false,
          bounds: {
            minX: -(cards.length - 1) * fullSlideWidth,
            maxX: 0,
          },
        })[0];

        dragInstance.addEventListener('dragend', () => {
          const direction = dragInstance.endX - dragInstance.startX;

          if (direction < 0) {
            setActiveIndex((prevIndex) =>
              Math.min(prevIndex + 1, cards.length - 1)
            );
          } else if (direction > 0) {
            setActiveIndex((prevIndex) => Math.max(prevIndex - 1, 0));
          }
        });

        return () => {
          if (dragInstance) {
            dragInstance.kill();
          }
        };
      }, 100);

      return () => clearTimeout(timer);
    },
    { dependencies: [isMobile, cards], scope: carouselContainerRef }
  );

  useGSAP(
    () => {
      if (!isMobile || !cardWidthRef.current) return;

      gsap.to(carouselContainerRef.current, {
        x: -activeIndex * cardWidthRef.current,
        duration: 0.8,
        ease: 'expo.out',
      });
    },
    { dependencies: [activeIndex, isMobile] }
  );

  return (
    <div className='page-wrapper thin-cards-wrapper row-gap-100 flex flex-column'>
      <p
        className={`uppercase full-width sec-nd-hp-p font-primary ${
          !isMobile ? 'half-width' : ''
        }`}
      >
        {titleGrotesk} {isMobile && <br />}
        <span className='font-secondary color-unset'> {titleItalic}</span>
      </p>

      {isMobile && (
        <div
          className='thin-cards-carousel-viewport'
          style={{ overflow: 'hidden' }}
        >
          <div
            ref={carouselContainerRef}
            className='thin-cards-carousel-container'
            style={{ display: 'flex', flexDirection: 'row' }}
          >
            {cards.map((card, index) => (
              <div
                key={card.titleItalic + card.titleGrotesk}
                className='thin-cards-carousel-slide'
                style={{
                  marginRight: index === cards.length - 1 ? 0 : `${CARD_GAP}px`,
                  flexShrink: 0,
                }}
              >
                <ImageCard
                  imageUrl={
                    isDarkMode
                      ? card.darkModeImageUrl || card.lightModeImageUrl
                      : card.lightModeImageUrl
                  }
                  {...card}
                  showVector={true}
                  imageHeight='15.27dvh'
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {!isMobile && (
        <div className='flex thin-cards full-width column-gap-40 row-gap-100 flex-wrap flex-row'>
          {cards.map((card) => (
            <ImageCard
              imageUrl={
                isDarkMode
                  ? card.darkModeImageUrl || card.lightModeImageUrl
                  : card.lightModeImageUrl
              }
              key={card.titleItalic + card.titleGrotesk}
              {...card}
              showVector={true}
              imageHeight='15.27dvh'
            />
          ))}
        </div>
      )}
      {isMobile && (
        <SmallStepIndicator
          currentStep={activeIndex + 1}
          totalSteps={cards.length}
          className='full-width'
          setCurrentStep={(index) => setActiveIndex(index - 1)}
        />
      )}
    </div>
  );
};

export default ThinImagesSection;
